/**
 * A same-device signal for the ADULT fast path in HubContent (App.tsx).
 * Packet R38 Fix 1: "the Hub's first open."
 *
 * The dancer-cookie check (getDancerSessionStatus, dancerSession.ts) is
 * `cache: 'no-store'` on purpose: it reads an HttpOnly cookie no client JS
 * can see, so it is the one truly unavoidable, uncacheable network round
 * trip on every boot. For the overwhelming majority of visits - an adult
 * who has never touched the dancer login on this device - that round trip
 * always answers "no dancer session," yet HubContent used to block EVERY
 * render on it regardless.
 *
 * This hint is the fix, and its meaning is deliberately the CONSERVATIVE
 * direction: present means "a real check just confirmed no dancer cookie
 * lives on this device." The safe default - nothing written yet, a private
 * window, storage disabled or thrown on - is always "unknown," which reads
 * as false here, so a fresh device always takes the slow, correct path:
 * wait for the check, exactly as before this fix. Only a device that has
 * been POSITIVELY told "no dancer cookie here" can ever skip the wait, and
 * even then only for an already-signed-in adult (see App.tsx's
 * HubContent) - a dancer session, a device with no hint yet, or a signed
 * out visitor is completely unaffected.
 *
 * NEVER the source of truth. The dancer-cookie fetch still runs on every
 * load, in the background when the fast path applies, and still clears a
 * stale cookie exactly as before (dancer-session-context.tsx). This hint
 * only decides whether RENDER waits for that fetch to land.
 *
 * Kept current from three places, all in dancer-session-context.tsx:
 *   - the status-check effect, on every real answer (present or absent),
 *   - a real dancer login (a cookie now definitely exists - clear it),
 *   - a real dancer logout (a cookie is now definitely gone - mark it).
 */

const NO_DANCER_HINT_KEY = 'ldg-hub-no-dancer-cookie-seen';

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage ?? null;
  } catch {
    // Private browsing, disabled storage, or a locked-down webview can
    // throw on ACCESS to window.localStorage itself, not just on a call -
    // same defensive shape as attachmentUploadResume.ts and previewRole.ts.
    return null;
  }
}

/**
 * True only when the last real check, login, or logout on this device
 * positively confirmed no dancer cookie is present. False - the fail-safe
 * default - covers "never checked," "storage unavailable," and "a dancer
 * cookie is or was just confirmed present": every one of those must keep
 * HubContent waiting for the real check, never guess.
 */
export function hasNoDancerHint(): boolean {
  const store = storage();
  if (!store) return false;
  try {
    return store.getItem(NO_DANCER_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Record a real, resolved answer: no dancer cookie is present. */
export function markNoDancerHint(): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(NO_DANCER_HINT_KEY, '1');
  } catch {
    // Storage full or blocked mid-write: the hint just won't persist, the
    // same fallback every other localStorage write in this app takes.
  }
}

/** Record a real, resolved answer: a dancer cookie IS present. */
export function clearNoDancerHint(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(NO_DANCER_HINT_KEY);
  } catch {
    // ignore
  }
}

/**
 * The actual render-gate decision for HubContent (App.tsx), pulled out as
 * its own pure function so it has a name a test can call directly instead
 * of only reading App.tsx as text. True only for the one case Fix 1
 * exists for: this device's hint positively says no dancer cookie, AND
 * there is already an adult Firebase session to render into. Everyone
 * else - signed out, an actual dancer session, or a device with no hint
 * yet - gets false, which keeps App.tsx waiting on `dancerSessionState`
 * exactly as it did before this fix.
 */
export function canRenderAdultOptimistically(params: {
  noDancerHint: boolean;
  hasFirebaseUser: boolean;
}): boolean {
  return params.noDancerHint && params.hasFirebaseUser;
}

/**
 * Fable review, PR #901, Medium 1. When canRenderAdultOptimistically()
 * already put the adult app on screen, a background getDancerSessionStatus()
 * failure (a 5xx, or simply offline) must not yank it back out into the
 * full-page AccessCheckError - the earlier confirmed "no dancer cookie"
 * evidence is still this device's best information, and a transient network
 * error is not new evidence against it. Reuses the exact same truth table as
 * the render gate on purpose: whatever rendered optimistically fails the
 * SAME way it would have rendered, never a stricter one.
 *
 * A NON-optimistic load - no hint yet, or no adult session to render into -
 * keeps the modal error exactly as before this fix: there was nothing on
 * screen for a failed check to yank away from, and the visitor still needs
 * to know the sign-in check itself did not finish.
 */
export function statusCheckFailureState(params: {
  noDancerHint: boolean;
  hasFirebaseUser: boolean;
}): 'signed-out' | 'error' {
  return canRenderAdultOptimistically(params) ? 'signed-out' : 'error';
}
